import { RULES_HTML } from "./menu";

/** Icone sablier (trait fin, discret) pour le chrono. */
const TIMER_ICON_SVG = `<svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M6 3h12"/><path d="M6 21h12"/><path d="M7 3v3c0 2.5 2.2 3.8 5 6-2.8 2.2-5 3.5-5 6v3"/><path d="M17 3v3c0 2.5-2.2 3.8-5 6 2.8 2.2 5 3.5 5 6v3"/></svg>`;

/** Icone boucle (compteur de coups). */
const MOVES_ICON_SVG = `<svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 1 0 3-6.7"/><path d="M3 4v5h5"/></svg>`;

/** Icone fleche courbe (bouton Annuler). */
const UNDO_ICON_SVG = `<svg viewBox="0 0 24 24" width="1.4em" height="1.4em" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 15 4 10l5-5"/><path d="M4 10h10.5a5.5 5.5 0 0 1 0 11H12"/></svg>`;

/** Icone ampoule (bouton Indice). */
const HINT_ICON_SVG = `<svg viewBox="0 0 24 24" width="1.4em" height="1.4em" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M9.5 18h5"/><path d="M10 21.5h4"/><path d="M12 2.5a6.7 6.7 0 0 0-3.8 12.2c.65.55 1.05 1.3 1.05 2.3h5.5c0-1 .4-1.75 1.05-2.3A6.7 6.7 0 0 0 12 2.5Z"/></svg>`;

export interface HudCallbacks {
  onUndo: () => void;
  onHint: () => void;
  onAutoComplete: () => void;
  onNewGame: () => void;
  onRestartSame: () => void;
  onOpenSettings: () => void;
  onQuitToMenu: () => void;
}

/**
 * HUD de partie, volontairement minimal et non anxiogene (cahier des charges
 * §8) : chrono qui monte (masquable), compteur de coups, boutons Annuler /
 * Indice / Terminer, et un bouton menu discret qui ouvre le parchemin de
 * pause (Nouvelle partie, Rejouer la donne, Regles, Reglages, Menu).
 */
export class GameHud {
  private el: HTMLDivElement;
  private timerVisible = true;

  constructor(root: HTMLElement, callbacks: HudCallbacks) {
    this.el = document.createElement("div");
    this.el.id = "game-hud";
    this.el.hidden = true;
    this.el.innerHTML = `
      <button class="hud-menu-btn" id="hud-menu-btn" type="button" aria-label="Menu" title="Menu">☰</button>
      <div class="hud-title">Le Solitaire</div>
      <div class="hud-stats">
        <span class="hud-stat" id="hud-timer">${TIMER_ICON_SVG} <span id="hud-timer-value">00:00</span></span>
        <span class="hud-stat">${MOVES_ICON_SVG} <span id="hud-moves">0</span></span>
      </div>
      <div class="hud-flash" id="hud-flash" hidden></div>
      <div class="hud-actions">
        <button class="wood-button concrete icon-btn" id="hud-undo" type="button" aria-label="Annuler" title="Annuler">${UNDO_ICON_SVG}</button>
        <button class="wood-button concrete icon-btn" id="hud-hint" type="button" aria-label="Indice" title="Indice">${HINT_ICON_SVG}</button>
        <button class="wood-button concrete" id="hud-finish" type="button" hidden>Terminer</button>
      </div>
      <div class="menu-modal" id="pause-modal" hidden>
        <div class="parchment-panel">
          <h2>Pause</h2>
          <div class="menu-buttons pause-buttons">
            <button class="wood-button primary" id="pause-resume">Reprendre</button>
            <button class="wood-button secondary" id="pause-new">Nouvelle donne</button>
            <button class="wood-button secondary" id="pause-restart">Rejouer cette donne</button>
            <button class="wood-button secondary" id="pause-rules">Règles</button>
            <button class="wood-button secondary" id="pause-settings">Réglages</button>
            <button class="wood-button secondary" id="pause-quit">Retour au menu</button>
          </div>
        </div>
      </div>
      <div class="menu-modal" id="hud-rules-modal" hidden>
        <div class="parchment-panel">
          <div>${RULES_HTML}</div>
          <button class="wood-button" id="hud-rules-close">Fermer</button>
        </div>
      </div>
    `;
    root.appendChild(this.el);

    const q = <T extends HTMLElement>(sel: string): T => this.el.querySelector<T>(sel)!;
    q<HTMLButtonElement>("#hud-undo").addEventListener("click", () => callbacks.onUndo());
    q<HTMLButtonElement>("#hud-hint").addEventListener("click", () => callbacks.onHint());
    q<HTMLButtonElement>("#hud-finish").addEventListener("click", () => callbacks.onAutoComplete());
    q<HTMLButtonElement>("#hud-menu-btn").addEventListener("click", () => this.openPause());
    q<HTMLButtonElement>("#pause-resume").addEventListener("click", () => this.closePause());
    q<HTMLButtonElement>("#pause-new").addEventListener("click", () => {
      this.closePause();
      callbacks.onNewGame();
    });
    q<HTMLButtonElement>("#pause-restart").addEventListener("click", () => {
      this.closePause();
      callbacks.onRestartSame();
    });
    q<HTMLButtonElement>("#pause-rules").addEventListener("click", () => {
      q<HTMLElement>("#hud-rules-modal").hidden = false;
    });
    q<HTMLButtonElement>("#hud-rules-close").addEventListener("click", () => {
      q<HTMLElement>("#hud-rules-modal").hidden = true;
    });
    q<HTMLButtonElement>("#pause-settings").addEventListener("click", () => {
      this.closePause();
      callbacks.onOpenSettings();
    });
    q<HTMLButtonElement>("#pause-quit").addEventListener("click", () => {
      this.closePause();
      callbacks.onQuitToMenu();
    });
  }

  show(): void {
    this.el.hidden = false;
  }

  hide(): void {
    this.el.hidden = true;
    this.closePause();
  }

  setTime(totalSeconds: number): void {
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    this.el.querySelector<HTMLElement>("#hud-timer-value")!.textContent =
      `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }

  setTimerVisible(visible: boolean): void {
    this.timerVisible = visible;
    this.el.querySelector<HTMLElement>("#hud-timer")!.style.visibility = visible ? "visible" : "hidden";
  }

  get isTimerVisible(): boolean {
    return this.timerVisible;
  }

  setMoves(moves: number): void {
    this.el.querySelector<HTMLElement>("#hud-moves")!.textContent = String(moves);
  }

  setUndoEnabled(enabled: boolean): void {
    this.el.querySelector<HTMLButtonElement>("#hud-undo")!.disabled = !enabled;
  }

  /** Bouton "Terminer" (auto-complete) : visible seulement quand la victoire est garantie. */
  setFinishVisible(visible: boolean): void {
    this.el.querySelector<HTMLButtonElement>("#hud-finish")!.hidden = !visible;
  }

  setActionsEnabled(enabled: boolean): void {
    for (const id of ["#hud-undo", "#hud-hint", "#hud-finish"]) {
      this.el.querySelector<HTMLButtonElement>(id)!.disabled = !enabled;
    }
  }

  /** Message central ephemere (indice, "Plus de coup possible", etc.). */
  flash(text: string, tone: "neutral" | "soft" = "neutral", durationMs = 2600): void {
    const flash = this.el.querySelector<HTMLElement>("#hud-flash")!;
    flash.textContent = text;
    flash.className = `hud-flash ${tone}`;
    flash.hidden = false;
    // Relance l'animation CSS meme si un flash precedent est encore affiche.
    void flash.offsetWidth;
    flash.classList.add("visible");
    window.clearTimeout(this.flashTimer);
    this.flashTimer = window.setTimeout(() => {
      flash.classList.remove("visible");
      flash.hidden = true;
    }, durationMs);
  }

  private flashTimer = 0;

  private openPause(): void {
    this.callbacksPausedOpen?.();
    this.el.querySelector<HTMLElement>("#pause-modal")!.hidden = false;
  }

  private closePause(): void {
    this.el.querySelector<HTMLElement>("#pause-modal")!.hidden = true;
  }

  /** Optionnel : notifie l'ouverture de la pause (son de clic). */
  callbacksPausedOpen: (() => void) | null = null;
}
